import { type SkipToken, skipToken } from '@apollo/client/react'
import { useAtomValue } from 'jotai'
import { getLocale, localeAtom } from '~/stores/locale'
import { baseApolloClient, cache } from './client'
import type { Artwork, GetArtworkQueryVariables } from './generated/graphql'
import {
  DeleteArtworkDocument,
  GetArtworkDocument,
  GetArtworkWithTaskDetailDocument,
  ListArtworksDocument,
  ListUserLikedArtworksDocument,
  MarkArtworkDocument,
  UpsertArtworkDocument,
} from './generated/graphql'
import {
  createFetchQueryHelper,
  createMutationHelper,
  createQueryHook,
  infiniteQueryHook,
  modifyQueryHook,
  pagedQueryHook,
} from './utils'

const _useGetArtwork = modifyQueryHook(createQueryHook(GetArtworkDocument), {
  reduce: r => r?.artwork,
})
export const useGetArtwork = (
  variables: GetArtworkQueryVariables['id'] | SkipToken,
  options?: Parameters<typeof _useGetArtwork>[1],
) => {
  const locale = useAtomValue(localeAtom)
  const input =
    variables !== skipToken
      ? { id: variables, tackLanguage: locale }
      : skipToken
  return _useGetArtwork(input, options)
}

export const updateGetArtwork = (
  variables: GetArtworkQueryVariables,
  newData: Artwork,
) => {
  cache.updateQuery(
    {
      query: GetArtworkDocument,
      variables: {
        id: variables.id,
        tackLanguage: getLocale(),
      },
    },
    () => {
      return { artwork: { __typename: 'Artwork' as const, ...newData } }
    },
  )
}
export const refetchGetArtwork = (id: string) => {
  baseApolloClient.refetchQueries({
    updateCache(cache) {
      cache.evict({
        id: cache.identify({ __typename: 'Artwork' as const, id }),
      })
    },
  })
}

export const fetchGetArtwork = createFetchQueryHelper(GetArtworkDocument)

const _useGetArtworkWithTaskDetail = modifyQueryHook(
  createQueryHook(GetArtworkWithTaskDetailDocument),
  {
    reduce: r => r?.artwork,
  },
)
export const useGetArtworkWithTaskDetail = (
  variables: Parameters<typeof _useGetArtworkWithTaskDetail>[0],
  options?: Parameters<typeof _useGetArtworkWithTaskDetail>[1],
) => {
  const locale = useAtomValue(localeAtom)
  const input =
    variables !== skipToken
      ? {
          ...variables,
          tackLanguage: locale,
        }
      : skipToken
  return _useGetArtworkWithTaskDetail(input, options)
}
export const fetchGetArtworkWithTaskDetail = createFetchQueryHelper(
  GetArtworkWithTaskDetailDocument,
)

const _useListArtworks = modifyQueryHook(
  createQueryHook(ListArtworksDocument),
  {
    reduce: r => r?.artworks,
  },
)
const useListArtworks = (
  variables: Parameters<typeof _useListArtworks>[0],
  options?: Parameters<typeof _useListArtworks>[1],
) => {
  const locale = useAtomValue(localeAtom)
  const input =
    variables !== skipToken
      ? {
          ...variables,
          tackLanguage: locale,
        }
      : skipToken
  return _useListArtworks(input, options)
}
export const useInfiniteListArtworks = infiniteQueryHook(useListArtworks)
export const usePagedListArtworks = pagedQueryHook(useListArtworks)

const _useListUserLikedArtworks = modifyQueryHook(
  createQueryHook(ListUserLikedArtworksDocument),
  {
    reduce: r => r?.user?.likedArtworks,
  },
)
export const useListUserLikedArtworks = (
  variables: Parameters<typeof _useListUserLikedArtworks>[0],
  options?: Parameters<typeof _useListUserLikedArtworks>[1],
) => {
  const locale = useAtomValue(localeAtom)
  const input =
    variables !== skipToken
      ? {
          ...variables,
          tackLanguage: locale,
        }
      : skipToken
  return _useListUserLikedArtworks(input, options)
}

export const fetchListArtworks = createFetchQueryHelper(ListArtworksDocument)

export const [useUpsertArtwork, executeUpsertArtwork] = createMutationHelper(
  UpsertArtworkDocument,
)

export const [useDeleteArtwork, executeDeleteArtwork] = createMutationHelper(
  DeleteArtworkDocument,
)

export const [useMarkArtwork, executeMarkArtwork] =
  createMutationHelper(MarkArtworkDocument)
